import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import ErrorPage403 from 'src/pages/error/403';
import {
  FinanceLimit,
  approveLimitRequest,
  cancelLimitRequest,
  listLimitRequests,
  listLimits,
  rejectLimitRequest,
  requestLimitChange,
} from '../../../services/financeTransferService';
import { RequestsTable } from '../../money-requests/RequestsTable';
import { useCurrentUserId, useRequestList } from '../../money-requests/useMoneyRequests';

const CHAIN = 'SOLANA';
const ASSETS = ['SOL', 'USDC'];
const AMOUNT = /^\d+(\.\d{0,6})?$/;
const isPositive = (v: string) => /^\d+(\.\d{1,6})?$/.test(v) && /[1-9]/.test(v);

const View = () => {
  const t = useTranslate();
  const notify = useNotify();
  const currentUserId = useCurrentUserId();
  const requests = useRequestList(listLimitRequests);
  const [limits, setLimits] = useState<FinanceLimit[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [asset, setAsset] = useState('USDC');
  const [perTransferMax, setPerTransferMax] = useState('');
  const [rolling24hMax, setRolling24hMax] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLimits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listLimits();
      setLimits(res.data ?? []);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadLimits();
  }, [loadLimits]);

  const openDialog = (forAsset: string) => {
    const existing = limits.find((l) => l.asset === forAsset);
    setAsset(forAsset);
    setPerTransferMax(existing?.perTransferMax ?? '');
    setRolling24hMax(existing?.rolling24hMax ?? '');
    setReason('');
    setOpen(true);
  };

  const orderOk =
    !isPositive(perTransferMax) ||
    !isPositive(rolling24hMax) ||
    Number(perTransferMax) <= Number(rolling24hMax);
  const valid = isPositive(perTransferMax) && isPositive(rolling24hMax) && orderOk;

  const submit = async () => {
    setSaving(true);
    try {
      await requestLimitChange({
        chain: CHAIN,
        asset,
        perTransferMax,
        rolling24hMax,
        reason: reason.trim() || undefined,
      });
      notify(
        t('module.financeLimits.requested', {
          _: 'Request created; another admin must approve it.',
        }),
        {
          type: 'success',
        }
      );
      setOpen(false);
      requests.load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PermissionGuard resource="finance-limits" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs heading={t('resource.financeLimits', { _: 'Limits' })} />
        <Card sx={{ mt: 3, p: 2 }}>
          <Stack spacing={3}>
            <Alert severity="info" variant="outlined">
              {t('module.financeLimits.intro', {
                _: 'Each asset has a per-transfer maximum and a rolling 24 hour maximum. An asset without a limit cannot be transferred. A limit changes only when one admin requests it and a different admin approves it.',
              })}
            </Alert>

            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h6">
                  {t('module.financeLimits.current', { _: 'Current limits' })} ({CHAIN})
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="outlined"
                  onClick={() => {
                    loadLimits();
                    requests.load();
                  }}
                >
                  {t('module.moneyRequests.reload', { _: 'Reload' })}
                </Button>
              </Stack>
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('module.financeLimits.asset', { _: 'Asset' })}</TableCell>
                      <TableCell align="right">
                        {t('module.financeLimits.perTransfer', { _: 'Per transfer max' })}
                      </TableCell>
                      <TableCell align="right">
                        {t('module.financeLimits.rolling', { _: 'Rolling 24 h max' })}
                      </TableCell>
                      <TableCell align="right">
                        {t('module.financeLimits.used', { _: 'Used in last 24 h' })}
                      </TableCell>
                      <TableCell align="right">
                        {t('module.moneyRequests.actions', { _: 'Actions' })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ASSETS.map((a) => {
                      const l = limits.find((x) => x.asset === a);
                      return (
                        <TableRow key={a}>
                          <TableCell>{a}</TableCell>
                          <TableCell align="right">
                            {loading
                              ? '...'
                              : l
                                ? l.perTransferMax
                                : t('module.financeLimits.notSet', { _: 'not set (refused)' })}
                          </TableCell>
                          <TableCell align="right">
                            {loading ? '...' : l ? l.rolling24hMax : ''}
                          </TableCell>
                          <TableCell align="right">
                            {loading ? '...' : l ? l.used24h : ''}
                          </TableCell>
                          <TableCell align="right">
                            <PermissionButton resource="finance-limits" action="request">
                              <Button size="small" variant="outlined" onClick={() => openDialog(a)}>
                                {t('module.financeLimits.requestChange', { _: 'Request change' })}
                              </Button>
                            </PermissionButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="h6">
                {t('module.financeLimits.requests', { _: 'Change requests' })}
              </Typography>
              <RequestsTable
                rows={requests.rows}
                loading={requests.loading}
                currentUserId={currentUserId}
                busyId={requests.busyId}
                approvePermission={{ resource: 'finance-limits', action: 'approve' }}
                cancelPermission={{ resource: 'finance-limits', action: 'request' }}
                summary={(r) => (
                  <span>
                    <b>{r.payload.asset}</b> per transfer {r.payload.perTransferMax}, rolling 24 h{' '}
                    {r.payload.rolling24hMax}
                  </span>
                )}
                onApprove={async (r) => {
                  await requests.run(
                    r.id,
                    () => approveLimitRequest(r.id),
                    t('module.moneyRequests.approved', { _: 'Approved' })
                  );
                  loadLimits();
                }}
                onReject={(r, why) =>
                  requests.run(
                    r.id,
                    () => rejectLimitRequest(r.id, why),
                    t('module.moneyRequests.rejected', { _: 'Rejected' })
                  )
                }
                onCancel={(r) =>
                  requests.run(
                    r.id,
                    () => cancelLimitRequest(r.id),
                    t('module.moneyRequests.cancelled', { _: 'Cancelled' })
                  )
                }
              />
            </Stack>
          </Stack>
        </Card>

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>
            {t('module.financeLimits.requestChange', { _: 'Request change' })}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                size="small"
                label={t('module.financeLimits.asset', { _: 'Asset' })}
                value={asset}
                onChange={(e) => openDialog(e.target.value)}
              >
                {ASSETS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                required
                label={t('module.financeLimits.perTransfer', { _: 'Per transfer max' })}
                value={perTransferMax}
                onChange={(e) =>
                  AMOUNT.test(e.target.value) || e.target.value === ''
                    ? setPerTransferMax(e.target.value)
                    : undefined
                }
                inputProps={{ inputMode: 'decimal' }}
              />
              <TextField
                size="small"
                required
                label={t('module.financeLimits.rolling', { _: 'Rolling 24 h max' })}
                value={rolling24hMax}
                onChange={(e) =>
                  AMOUNT.test(e.target.value) || e.target.value === ''
                    ? setRolling24hMax(e.target.value)
                    : undefined
                }
                inputProps={{ inputMode: 'decimal' }}
                error={!orderOk}
                helperText={
                  !orderOk
                    ? t('module.financeLimits.invalidOrder', {
                        _: 'The per-transfer maximum cannot exceed the rolling 24 h maximum.',
                      })
                    : undefined
                }
              />
              <TextField
                size="small"
                label={t('module.moneyRequests.reason', { _: 'Reason' })}
                value={reason}
                inputProps={{ maxLength: 500 }}
                onChange={(e) => setReason(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{t('actions.cancel')}</Button>
            <PermissionButton resource="finance-limits" action="request">
              <Button variant="contained" disabled={saving || !valid} onClick={submit}>
                {t('module.financeAllowlist.submitRequest', { _: 'Submit request' })}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
